import React, { useEffect, useState, useCallback } from "react";
import { Eye, Trash2, Image, Video, Clock, Users } from "lucide-react";
import { Pagination, Stack } from "@mui/material";
import { Loader } from "./loader";
import api from "../../api";
import moment from "moment";
import "./AdminStories.css";

export default function AdminStories() {
  const [stories, setStories]   = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [loader, setLoader]     = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [preview, setPreview]   = useState(null);
  const limit = 20;

  const load = useCallback(async () => {
    try {
      setLoader(true);
      const res = await api.getStoriesAdmin({ page, limit });
      setStories(res.stories || []);
      setTotal(res.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoader(false);
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this story?")) return;
    setDeleting(id);
    try {
      await api.deleteStory(id);
      setStories(prev => prev.filter(s => s._id !== id));
      setTotal(t => t - 1);
    } catch (e) {
      alert("Failed to delete story");
    } finally {
      setDeleting(null);
    }
  };

  const isLive = (story) => new Date(story.expiresAt) > new Date();

  const totalViews = stories.reduce((sum, s) => sum + (s.views || 0), 0);
  const liveCount  = stories.filter(isLive).length;

  return (
    <div className="stories-admin-page">
      <div className="stories-admin-header">
        <h4 className="stories-admin-title">Stories</h4>
        <div className="stories-stats-row">
          <div className="stories-stat-card">
            <span className="stories-stat-value">{total}</span>
            <span className="stories-stat-label">Total</span>
          </div>
          <div className="stories-stat-card live">
            <span className="stories-stat-value">{liveCount}</span>
            <span className="stories-stat-label">Live Now</span>
          </div>
          <div className="stories-stat-card">
            <Eye size={16} />
            <span className="stories-stat-value">{totalViews}</span>
            <span className="stories-stat-label">Views (this page)</span>
          </div>
        </div>
      </div>

      {loader ? (
        <Loader />
      ) : stories.length === 0 ? (
        <div className="stories-empty">No stories found.</div>
      ) : (
        <>
          <div className="stories-grid">
            {stories.map(story => (
              <div key={story._id} className={`story-card ${isLive(story) ? "live" : "expired"}`}>

                {/* Thumbnail */}
                <div
                  className="story-thumb"
                  onClick={() => setPreview(story)}
                  title="Preview"
                >
                  {story.type === "image" ? (
                    <img src={story.imageUrl} alt="story" className="story-thumb-img" />
                  ) : story.thumbnailUrl ? (
                    <img src={story.thumbnailUrl} alt="thumb" className="story-thumb-img" />
                  ) : (
                    <div className="story-thumb-placeholder" />
                  )}
                  <div className="story-thumb-overlay">
                    {story.type === "image" ? <Image size={22} /> : <Video size={22} />}
                  </div>
                </div>

                {/* Info */}
                <div className="story-info">
                  <div className="story-badges">
                    <span className={`story-type-badge ${story.type}`}>
                      {story.type === "image" ? "Image" : "Video"}
                    </span>
                    <span className={`story-status-badge ${isLive(story) ? "live" : "expired"}`}>
                      {isLive(story) ? "Live" : "Expired"}
                    </span>
                  </div>

                  <div className="story-meta">
                    <span><Clock size={12} /> {moment(story.createdAt).fromNow()}</span>
                    <span>
                      <Clock size={12} /> expires {moment(story.expiresAt).fromNow()}
                    </span>
                  </div>

                  <div className="story-views-row">
                    <span><Eye size={13} /> {story.views || 0} views</span>
                    <span><Users size={13} /> {story.uniqueViewers || 0} unique</span>
                    {story.duration > 0 && (
                      <span>{Math.round(story.duration)}s</span>
                    )}
                  </div>

                  <button
                    className="story-delete-btn"
                    onClick={() => handleDelete(story._id)}
                    disabled={deleting === story._id}
                  >
                    <Trash2 size={14} />
                    {deleting === story._id ? "Deleting…" : "Remove"}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {total > limit && (
            <Stack alignItems="center" mt={3}>
              <Pagination
                count={Math.ceil(total / limit)}
                page={page}
                onChange={(_, v) => setPage(v)}
                color="primary"
              />
            </Stack>
          )}
        </>
      )}

      {/* Preview modal */}
      {preview && (
        <div className="story-preview-backdrop" onClick={() => setPreview(null)}>
          <div className="story-preview-modal" onClick={e => e.stopPropagation()}>
            <button className="story-preview-close" onClick={() => setPreview(null)}>✕</button>
            {preview.type === "image" ? (
              <img src={preview.imageUrl} alt="preview" className="story-preview-media" />
            ) : (
              <video
                src={preview.videoUrl}
                controls
                autoPlay
                className="story-preview-media"
              />
            )}
            <div className="story-preview-meta">
              <span><Eye size={14} /> {preview.views || 0} views · {preview.uniqueViewers || 0} unique</span>
              <span>Expires {moment(preview.expiresAt).fromNow()}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
